import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";

const PopupContainer = styled.div`
  position: relative;
`;

const getPositionStyles = (position, arrow, backgroundColor) => {
  const arrowBase = `
    content: '';
    position: absolute;
    border-width: 5px;
    border-style: solid;
  `;

  switch (position) {
    case "top":
      return `
        bottom: 125%;
        left: 50%;
        transform: translateX(-50%);
        ${
          arrow
            ? `
          &::after {
            ${arrowBase}
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-color: ${backgroundColor} transparent transparent transparent;
          }
        `
            : ""
        }
      `;
    case "top-left":
      return `
        bottom: 125%;
        left: 0;
        transform: translateX(0);
        ${
          arrow
            ? `
          &::after {
            ${arrowBase}
            top: 100%;
            left: 10px;
            transform: translateX(-50%);
            border-color: ${backgroundColor} transparent transparent transparent;
          }
        `
            : ""
        }
      `;
    case "top-right":
      return `
        bottom: 125%;
        right: 0;
        transform: translateX(0);
        ${
          arrow
            ? `
          &::after {
            ${arrowBase}
            top: 100%;
            right: 10px;
            transform: translateX(-50%);
            border-color: ${backgroundColor} transparent transparent transparent;
          }
        `
            : ""
        }
      `;
    case "bottom":
      return `
        top: 125%;
        left: 50%;
        transform: translateX(-50%);
        ${
          arrow
            ? `
          &::after {
            ${arrowBase}
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-color: transparent transparent ${backgroundColor} transparent;
          }
        `
            : ""
        }
      `;
    case "bottom-left":
      return `
        top: 125%;
        left: 0;
        transform: translateX(0);
        ${
          arrow
            ? `
          &::after {
            ${arrowBase}
            bottom: 100%;
            left: 10px;
            transform: translateX(-50%);
            border-color: transparent transparent ${backgroundColor} transparent;
          }
        `
            : ""
        }
      `;
    case "bottom-right":
      return `
        top: 125%;
        right: 0;
        transform: translateX(0);
        ${
          arrow
            ? `
          &::after {
            ${arrowBase}
            bottom: 100%;
            right: 10px;
            transform: translateX(-50%);
            border-color: transparent transparent ${backgroundColor} transparent;
          }
        `
            : ""
        }
      `;
    case "right":
      return `
        top: 50%;
        left: calc(100% + 5px);
        transform: translateY(-50%);
        ${
          arrow
            ? `
          &::after {
            ${arrowBase}
            top: 50%;
            left: -9px;
            transform: translateY(-50%) rotate(180deg);
            border-color: transparent transparent transparent ${backgroundColor};
          }
        `
            : ""
        }
      `;
    case "right-top":
      return `
        top: 0;
        left: calc(100% + 5px);
        transform: translateY(0);
        ${
          arrow
            ? `
          &::after {
            ${arrowBase}
            top: 10px;
            left: -9px;
            transform: translateY(0) rotate(180deg);
            border-color: transparent transparent transparent ${backgroundColor};
          }
        `
            : ""
        }
      `;
    case "right-bottom":
      return `
        bottom: 0;
        left: calc(100% + 5px);
        transform: translateY(0);
        ${
          arrow
            ? `
          &::after {
            ${arrowBase}
            bottom: 10px;
            left: -9px;
            transform: translateY(0) rotate(180deg);
            border-color: transparent transparent transparent ${backgroundColor};
          }
        `
            : ""
        }
      `;
    case "left":
      return `
        top: 50%;
        right: calc(100% + 5px);
        transform: translateY(-50%);
        ${
          arrow
            ? `
          &::after {
            ${arrowBase}
            top: 50%;
            right: -10px;
            transform: translateY(-50%) rotate(180deg);
            border-color: transparent ${backgroundColor} transparent transparent;
          }
        `
            : ""
        }
      `;
    case "left-top":
      return `
        top: 0;
        right: calc(100% + 5px);
        transform: translateY(0);
        ${
          arrow
            ? `
          &::after {
            ${arrowBase}
            top: 10px;
            right: -10px;
            transform: translateY(0) rotate(180deg);
            border-color: transparent ${backgroundColor} transparent transparent;
          }
        `
            : ""
        }
      `;
    case "left-bottom":
      return `
        bottom: 0;
        right: calc(100% + 5px);
        transform: translateY(0);
        ${
          arrow
            ? `
          &::after {
            ${arrowBase}
            bottom: 10px;
            right: -10px;
            transform: translateY(0) rotate(180deg);
            border-color: transparent ${backgroundColor} transparent transparent;
          }
        `
            : ""
        }
      `;
    default:
      return "";
  }
};

const PopupContent = styled.div`
  visibility: ${(props) => (props.isVisible ? "visible" : "hidden")};
  background-color: ${(props) => props.backgroundColor || "white"};
  color: ${(props) => props.textColor || "black"};
  border-radius: 5px;
  padding: 8px 8px;
  position: absolute;
  z-index: 999;
  ${(props) =>
    getPositionStyles(props.position, props.arrow, props.backgroundColor)}
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
`;

const Popup = ({
  trigger,
  content,
  position = "top",
  hoverable = true,
  className,
  textColor,
  backgroundColor = "white",
  arrow = true,
  onClose = () => {}, // Add onClose prop with default empty function
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);

  const handleMouseEnter = () => {
    if (hoverable) {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (hoverable) {
      setIsVisible(false);
    }
  };

  const handleClick = () => {
    if (!hoverable) {
      setIsVisible(!isVisible);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        contentRef.current &&
        !contentRef.current.contains(event.target)
      ) {
        setIsVisible(false);
        onClose(); // Call onClose function when popup is closed
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [triggerRef, contentRef, onClose]);

  return (
    <PopupContainer
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      className={className}
      {...props}
    >
      {trigger}
      <PopupContent
        ref={contentRef}
        isVisible={isVisible}
        position={position}
        textColor={textColor}
        backgroundColor={backgroundColor}
        arrow={arrow}
        onClick={(e) => e.stopPropagation()} // Stop event propagation
      >
        {content}
      </PopupContent>
    </PopupContainer>
  );
};

export default Popup;
